// Top-level container for the Settings > Integrations > Carriers
// section. Sits directly below Sales Channels as a sibling collapsible
// group (the SalesChannelsAdmin header comment reserved exactly this
// spot). Loads /admin/carriers and renders each carrier as a
// collapsible card — mapped over the array, so a second courier is a
// BE seed row away, not a FE change.

import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import CarrierCard from "./CarrierCard";
import type { CarrierConfig } from "./types";
import "../../../styles/admin-integrations.css";

// One-line hint under each carrier's card, keyed by carriers.carrier_code.
const CARRIER_HINT_KEY: Record<string, string> = {
    apc: "carriers_apc_hint",
    inpost: "carriers_inpost_hint",
    dpd: "carriers_dpd_hint",
    cargus: "carriers_cargus_hint",
    palletways: "carriers_palletways_hint",
    dbschenker: "carriers_dbschenker_hint",
    jas: "carriers_jas_hint",
    raben: "carriers_raben_hint",
};

const CarriersAdmin = () => {
    const { t } = useTranslation();
    const [carriers, setCarriers] = useState<CarrierConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await apiFetch("/admin/carriers", {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setCarriers(Array.isArray(json?.carriers) ? json.carriers : []);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setLoadError(msg);
            toast.error(t("carriers_load_failed"), { toastId: "carriers-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const handleCarrierUpdated = useCallback((next: CarrierConfig) => {
        setCarriers(curr => curr.map(c => (c.id === next.id ? next : c)));
    }, []);

    return (
        <details className="admin-integrations-group" id="carriers">
            <summary className="admin-integrations-group-summary">
                <span className="admin-integrations-group-title">
                    {t("carriers_heading")}
                </span>
                <span className="admin-integrations-group-count">
                    {carriers.length}
                </span>
            </summary>

            <div className="admin-integrations-group-body">
                {loading ? (
                    <div className="admin-integrations-loading">
                        <Loader2 size={18} className="admin-integrations-spinner" /> {t("loading")}
                    </div>
                ) : loadError ? (
                    <p className="admin-integrations-error">{loadError}</p>
                ) : (
                    <div className="admin-integrations">
                        {carriers.map((carrier) => (
                            <CarrierCard
                                key={carrier.id}
                                carrier={carrier}
                                hint={CARRIER_HINT_KEY[carrier.carrierCode] ? t(CARRIER_HINT_KEY[carrier.carrierCode]) : undefined}
                                onUpdated={handleCarrierUpdated}
                            />
                        ))}
                    </div>
                )}
            </div>
        </details>
    );
};

export default CarriersAdmin;
